import { inject, Injectable } from '@angular/core';
import { RvoAnimal, SelectionMarkColor } from '@tcc-mono/vandijk/shared/data-access/models';
import { LocalStorageService } from '@tcc-mono/shared/ui/theming';
import { Observable } from 'rxjs';
import { EnrichedRvoAnimal } from './animal-selection-animal-dialog.types';

/** Eerste batch sync (direct zichtbaar), daarna chunks via rAF. */
const FIRST_SYNC_COUNT = 40;
const CHUNK_SIZE = 120;

type EnrichParams = {
  suggestColor: (dateOfBirth: string | undefined, colors: SelectionMarkColor[]) => string | null;
  ageInDays: (dateOfBirth: string) => number;
};

@Injectable({
  providedIn: 'root'
})
export class KspRvoPoolCacheService {
  private readonly _storage = inject(LocalStorageService);

  private readonly _parsedByKey = new Map<string, RvoAnimal[]>();
  private readonly _enrichedByKey = new Map<string, Map<string, EnrichedRvoAnimal>>();
  /** Volledige verrijkte lijst (volgorde behouden) — instant bij heropen dialog. */
  private readonly _enrichedListByKey = new Map<string, EnrichedRvoAnimal[]>();

  public hasCachedAnimals(storageKey: string): boolean {
    if (this._parsedByKey.has(storageKey)) {
      return (this._parsedByKey.get(storageKey)?.length ?? 0) > 0;
    }
    const cached = this._storage.get<string>(storageKey);
    return typeof cached === 'string' && cached.length > 2 && cached.startsWith('[');
  }

  public getParsedAnimals(storageKey: string): RvoAnimal[] {
    const memory = this._parsedByKey.get(storageKey);
    if (memory) {
      return memory;
    }

    const cached = this._storage.get<string>(storageKey);
    if (!cached) {
      return [];
    }

    try {
      const parsed = JSON.parse(cached) as RvoAnimal[];
      if (Array.isArray(parsed)) {
        this._parsedByKey.set(storageKey, parsed);
        return parsed;
      }
    } catch {
      // ignore corrupt cache
    }

    return [];
  }

  public getEnrichedList(storageKey: string): EnrichedRvoAnimal[] | null {
    const list = this._enrichedListByKey.get(storageKey);
    const parsed = this._parsedByKey.get(storageKey);
    if (!list?.length || !parsed?.length || list.length !== parsed.length) {
      return null;
    }
    return list;
  }

  public setParsedAnimals(storageKey: string, animals: RvoAnimal[]): void {
    const prev = this._parsedByKey.get(storageKey);
    this._parsedByKey.set(storageKey, animals);
    if (prev !== animals) {
      this._enrichedByKey.delete(storageKey);
      this._enrichedListByKey.delete(storageKey);
    }
    this._storage.set({
      key: storageKey,
      value: JSON.stringify(animals)
    });
  }

  public clear(storageKey: string): void {
    this._parsedByKey.delete(storageKey);
    this._enrichedByKey.delete(storageKey);
    this._enrichedListByKey.delete(storageKey);
    this._storage.clear(storageKey);
  }

  /** Herbereken alleen kleurmarkering (geen volledige enrich-restart). */
  public reapplyColors(
    storageKey: string,
    colors: SelectionMarkColor[],
    suggestColor: (dateOfBirth: string | undefined, colors: SelectionMarkColor[]) => string | null
  ): EnrichedRvoAnimal[] | null {
    const list = this._enrichedListByKey.get(storageKey);
    const map = this._enrichedByKey.get(storageKey);
    if (!list?.length || !map) {
      return null;
    }

    const updated = list.map((animal) => {
      const key = this._animalKey(animal);
      const next: EnrichedRvoAnimal = {
        ...animal,
        suggestedColorValue: suggestColor(animal.geboorteDatum, colors)
      };
      map.set(key, next);
      return next;
    });

    this._enrichedListByKey.set(storageKey, updated);
    return updated;
  }

  public enrichAnimalsChunked(
    storageKey: string,
    animals: RvoAnimal[],
    colors: SelectionMarkColor[],
    params: EnrichParams
  ): Observable<EnrichedRvoAnimal[]> {
    return new Observable((subscriber) => {
      let cancelled = false;

      if (!animals.length) {
        subscriber.next([]);
        subscriber.complete();
        return () => {
          cancelled = true;
        };
      }

      let enrichedMap = this._enrichedByKey.get(storageKey);
      if (!enrichedMap) {
        enrichedMap = new Map<string, EnrichedRvoAnimal>();
        this._enrichedByKey.set(storageKey, enrichedMap);
      }

      let index = 0;
      const ordered: EnrichedRvoAnimal[] = [];

      const enrichOne = (animal: RvoAnimal): EnrichedRvoAnimal => {
        const key = this._animalKey(animal);
        let enriched = enrichedMap!.get(key);

        if (!enriched) {
          const ageInDays = params.ageInDays(animal.geboorteDatum);
          enriched = {
            ...animal,
            ageInDays,
            suggestedColorValue: params.suggestColor(animal.geboorteDatum, colors)
          };
          enrichedMap!.set(key, enriched);
        } else {
          enriched = {
            ...enriched,
            suggestedColorValue: params.suggestColor(enriched.geboorteDatum, colors)
          };
          enrichedMap!.set(key, enriched);
        }

        return enriched;
      };

      const processCount = (count: number): void => {
        const end = Math.min(index + count, animals.length);
        for (; index < end; index++) {
          ordered.push(enrichOne(animals[index]));
        }
        subscriber.next([...ordered]);
      };

      const processChunkAsync = (): void => {
        if (cancelled) {
          return;
        }

        processCount(CHUNK_SIZE);

        if (index < animals.length) {
          requestAnimationFrame(processChunkAsync);
        } else {
          this._enrichedListByKey.set(storageKey, [...ordered]);
          subscriber.complete();
        }
      };

      // Eerste batch direct sync — eerste dieren meteen zichtbaar
      processCount(FIRST_SYNC_COUNT);

      if (index < animals.length) {
        requestAnimationFrame(processChunkAsync);
      } else {
        this._enrichedListByKey.set(storageKey, [...ordered]);
        subscriber.complete();
      }

      return () => {
        cancelled = true;
      };
    });
  }

  private _animalKey(animal: RvoAnimal): string {
    return `${animal.dierLandcode ?? ''}${animal.dierLevensnummer ?? ''}`;
  }
}
